import React, { useCallback } from 'react';
import type { FormikProps, FormikHelpers } from 'formik';
import { Formik, Form } from 'formik';
import { useTranslation } from 'next-i18next';
import * as Yup from 'yup';
import { DialogActions, DialogContent, FormControlLabel, Radio, RadioGroup } from '@mui/material';

import Box from '@mui/material/Box';

import Dialog from 'src/components/ui/Dialog';
import DialogTitle from 'src/components/ui/DialogTitle';
import Typography from 'src/components/ui/Typography';
import TextField from 'src/components/ui/TextField';
import Button from 'src/components/ui/Button';
import ConfirmActionModal from 'src/components/ui/ConfirmActionModal';

import { routePathFavorites, routePathProfileCompanyActivityWithTabSelect } from 'src/constants/router';
import { colours } from 'src/constants/colours';

import useAlerts from 'src/hooks/useAlerts';
import useUser from 'src/hooks/useUser';
import useAppRouter from 'src/hooks/useAppRouter';
import type { ModalProps } from 'src/hooks/useModalOpenState';
import useModalOpenState from 'src/hooks/useModalOpenState';

import { GalleryRole, GalleryInviteeType, type Gallery, type User } from 'src/types/__generated__/graphql';
import { styledComponent } from 'src/utils/styled';
import useDynamicLogEvents from 'src/hooks/useDynamicLogEvents';
import { AnalyticsEvent } from 'src/utils/analytics';
import type { MultipleUserPickerOption } from 'src/components/ui/MultipleUserPicker';
import { MultipleUserPicker } from 'src/components/ui/MultipleUserPicker';
import { useManageGallery } from '../hooks/useManageGallery';
import { useSearchCollaborators } from '../hooks/useSearchCollaborators';
import { emailRFC5322RegEx } from 'src/constants/regexes';

const StyledDialog = styledComponent(Dialog)`
    .MuiPaper-root {
        width: 28rem;
        max-width: 100%;
        max-height: 90%;
    }
    .MuiDialogContent-root {
        padding: 0 1.5rem;
    }
    .MuiDialogTitle-root {
        padding: 1.5rem 1.5rem 0.5rem 1.5rem;
    }
    .MuiDialogActions-root {
        padding: 1.5rem;
        border-top: 0.0625rem solid ${colours.grey.G200};
        justify-content: center;
    }
`;

StyledDialog.defaultProps = {
    maxWidth: 'xs',
    fullWidth: true,
};

const MultipleUserPickerWrapper = styledComponent(Box)`
    .MuiAutocomplete-listbox {
        overflow: unset;
    }
`;

const StyledFormControlLabel = styledComponent(FormControlLabel)`
    justify-content: space-between;
    margin-left: 0;
    margin-right: 0;
    pointer-events: all;
`;

type GalleryData = {
    id: Gallery['id'];
    name: Gallery['name'];
    isSecret: Gallery['isSecret'];
    owner?: {
        userId?: string | null;
        companyId?: string | null;
    } | null;
    viewerPermissions: Gallery['viewerPermissions'];
};

type FormValues = Pick<Gallery, 'name' | 'isSecret'> & {
    id?: string;
};

interface Props extends ModalProps {
    gallery?: GalleryData;
    adIdToAddInGalleryAfterCreate?: string;
    postIdToAddInGalleryAfterCreate?: string;
    newsPostIdToAddInGalleryAfterCreate?: string;
    companyId?: string;
    redirectAfterCreate?: boolean;
    showCollaboratorsSelection?: boolean;
    onPostUpdateGallery?: () => void;
}

export const ManageGalleryModal: React.FC<Props> = ({
    open,
    onClose,
    gallery,
    adIdToAddInGalleryAfterCreate,
    postIdToAddInGalleryAfterCreate,
    newsPostIdToAddInGalleryAfterCreate,
    companyId,
    redirectAfterCreate,
    showCollaboratorsSelection,
    onPostUpdateGallery,
}) => {
    const { t } = useTranslation(['favorites', 'common']);
    const { addAlert } = useAlerts();
    const router = useAppRouter();
    const currentUser = useUser();
    const { logEventDynamic } = useDynamicLogEvents();
    const { createGallery, updateGallery, deleteGallery } = useManageGallery();
    const {
        query,
        setQuery,
        queryIsEmpty,
        selectedUsers,
        setSelectedUsers,
        users,
        loading: searchCollaboratorsLoading,
    } = useSearchCollaborators({
        skip: !open || !showCollaboratorsSelection,
        galleryId: gallery?.id,
    });

    const { props: confirmModalProps, open: openConfirmModal } = useModalOpenState();

    const handleDeleteGallery = async (id: string) => {
        try {
            await deleteGallery(id);
            if (
                gallery?.owner?.companyId === currentUser?.affiliatedCompany?.id &&
                currentUser?.isAdminOfAffiliatedCompany
            ) {
                // Here we replace so that the user does not end up on a gallery that no longer exists.
                await router.replace(`${routePathProfileCompanyActivityWithTabSelect}${'galleries'}`);
            } else {
                await router.replace(routePathFavorites);
            }
        } catch (error: unknown) {
            addAlert({
                type: 'error',
                message: String(error),
            });
        }
    };

    const handleChangeVisibility = (
        e: React.ChangeEvent<HTMLInputElement>,
        setFieldValue: FormikHelpers<FormValues>['setFieldValue'],
    ) => {
        const isSecret: boolean = e.target.value === 'true';
        setFieldValue('isSecret', isSecret);
    };

    const selectSuccessMessageToDisplay = (values: FormValues) => {
        if (values.id) {
            if (gallery?.name !== values.name) {
                return t('favorites:galleryUpdatedSuccess.defaultMessage', {
                    galleryName: values.name,
                });
            } else {
                return values.isSecret
                    ? t('favorites:galleryUpdatedSuccess.nowPrivate', {
                          galleryName: values.name,
                      })
                    : t('favorites:galleryUpdatedSuccess.nowPublic', {
                          galleryName: values.name,
                      });
            }
        }
        return t('favorites:galleryCreatedSuccess');
    };

    const optionCardRightArea = useCallback(
        (option: MultipleUserPickerOption, selected: boolean) => (
            <MultipleUserPickerOptionCardRightArea optionSelected={selected} />
        ),
        [],
    );

    const handleSubmitForm = async (values: FormValues, { resetForm }: FormikHelpers<FormValues>) => {
        try {
            let addContentToGalleryAfterCreate = false;
            let createdGallery;
            logEventDynamic(AnalyticsEvent.ParametersConfirmation);
            if (values.id) {
                await updateGallery({
                    id: values.id,
                    name: values.name,
                    isSecret: values.isSecret,
                });
                onPostUpdateGallery?.();
            } else {
                addContentToGalleryAfterCreate = Boolean(
                    adIdToAddInGalleryAfterCreate ??
                        postIdToAddInGalleryAfterCreate ??
                        newsPostIdToAddInGalleryAfterCreate,
                );
                createdGallery = await createGallery({
                    name: values.name,
                    isSecret: values.isSecret,
                    companyId,
                    contentToAdd: addContentToGalleryAfterCreate
                        ? {
                              adId: adIdToAddInGalleryAfterCreate,
                              postId: postIdToAddInGalleryAfterCreate,
                              newsPostId: newsPostIdToAddInGalleryAfterCreate,
                          }
                        : undefined,
                    invitations: selectedUsers.map((user) => ({
                        role: GalleryRole.Editor,
                        invitee:
                            user.type === 'user'
                                ? {
                                      type: GalleryInviteeType.User,
                                      value: user.value.id,
                                  }
                                : {
                                      type: GalleryInviteeType.Email,
                                      value: user.value,
                                  },
                    })),
                });
            }
            resetForm();
            onClose();

            // We only want to show the success message if we are not adding content to the gallery after creating it
            // because we already show a success message when adding content to the gallery
            if (!addContentToGalleryAfterCreate) {
                addAlert({
                    type: 'success',
                    message: selectSuccessMessageToDisplay(values),
                });
            }
            if (redirectAfterCreate && createdGallery) {
                await router.replace(`${routePathFavorites}/${createdGallery.id}`);
            }
        } catch (error: unknown) {
            addAlert({
                type: 'error',
                message: String(error),
            });
        }
    };

    const yupSchema = Yup.object().shape({
        name: Yup.string()
            .required(t('common:errors.required'))
            .max(40, t('common:errors.tooLong', { number: '40' })),
    });

    const initialValues: FormValues = {
        id: gallery?.id,
        name: gallery?.name ?? '',
        isSecret: gallery?.isSecret ?? true,
    };

    const multipleUserPickerOptions = [
        ...(emailRFC5322RegEx.test(query) ? [{ type: 'email' as const, value: query }] : []),
        ...users.map((user) => ({ type: 'user' as const, value: user.user })),
    ];

    const enableDelete = initialValues.id && gallery?.viewerPermissions.canDelete;
    const showLabel = queryIsEmpty && selectedUsers.length < 1;
    return (
        <Formik
            initialValues={initialValues}
            validationSchema={yupSchema}
            onSubmit={handleSubmitForm}
            enableReinitialize
        >
            {({
                isValid,
                dirty,
                handleSubmit,
                resetForm,
                values,
                setFieldValue,
                isSubmitting,
            }: FormikProps<FormValues>) => (
                <Form>
                    <StyledDialog
                        open={open}
                        onClose={() => {
                            resetForm();
                            onClose();
                        }}
                        data-target-id="manage-gallery-modal"
                    >
                        <DialogTitle
                            onClose={() => {
                                resetForm();
                                onClose();
                            }}
                        >
                            <Typography variant="h2" style={{ color: colours.black.main }}>
                                {values.id ? t('favorites:parameters') : t('favorites:createGallery')}
                            </Typography>
                        </DialogTitle>
                        <DialogContent>
                            <TextField
                                InputProps={{
                                    inputProps: {
                                        'data-target-id': 'manage-gallery-input',
                                        maxLength: 40,
                                    },
                                }}
                                name="name"
                                label={t('favorites:name')}
                                data-target-id="manage-gallery-input"
                            />
                            <Box mb="1rem">
                                <Typography variant="h5" style={{ color: colours.primary.O87 }}>
                                    {t('favorites:visibility')}
                                </Typography>
                                <RadioGroup
                                    onChange={(e) => handleChangeVisibility(e, setFieldValue)}
                                    value={values.isSecret}
                                >
                                    <StyledFormControlLabel
                                        data-target-id="choose-private-radio"
                                        name="isSecret"
                                        label={t('favorites:privateGalleryWithInfo')}
                                        labelPlacement="start"
                                        control={<Radio />}
                                        value
                                    />
                                    <StyledFormControlLabel
                                        data-target-id="choose-public-radio"
                                        name="isSecret"
                                        value={false}
                                        label={t('favorites:publicGallery')}
                                        labelPlacement="start"
                                        control={<Radio />}
                                    />
                                </RadioGroup>
                            </Box>
                            {showCollaboratorsSelection && (
                                <Box mb="1rem">
                                    <Typography variant="h5" mb="1rem" style={{ color: colours.primary.O87 }}>
                                        {t('favorites:addCollaborators')}
                                    </Typography>
                                    <MultipleUserPickerWrapper>
                                        <MultipleUserPicker
                                            name="users"
                                            onChangeTextField={(e) => setQuery(e.target.value)}
                                            onChangeSelectedUsers={(e, items) => setSelectedUsers(items ?? [])}
                                            options={multipleUserPickerOptions}
                                            showLabel={showLabel}
                                            loading={searchCollaboratorsLoading}
                                            fixedHeight="16rem"
                                            textFieldMaxHeight="6.5rem"
                                            emptyStateIconSize="4.5rem"
                                            optionCardRightArea={optionCardRightArea}
                                            optionDisabled={() => true} // Disable the option because we dont want the whole card to be clickable
                                        />
                                    </MultipleUserPickerWrapper>
                                </Box>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Box width="100%" display="flex" justifyContent={values.id ? 'space-between' : 'flex-end'}>
                                {values.id && (
                                    <>
                                        <Button
                                            data-target-id="manage-gallery-delete"
                                            onClick={openConfirmModal}
                                            variant="outlined"
                                            color="error"
                                            disabled={!enableDelete}
                                        >
                                            {t('favorites:delete')}
                                        </Button>
                                        <ConfirmActionModal
                                            open={confirmModalProps.open}
                                            title={t('favorites:deleteGalleryConfirmTitle')}
                                            description={t('favorites:deleteGalleryConfirmText')}
                                            confirm={t('favorites:delete')}
                                            onConfirm={async () => {
                                                confirmModalProps.onClose();
                                                await handleDeleteGallery(values.id as string);
                                            }}
                                            onCloseOrCancel={() => confirmModalProps.onClose()}
                                        />
                                    </>
                                )}
                                <Box display="flex" gap="0.5rem">
                                    <Button
                                        data-target-id="manage-gallery-cancel"
                                        onClick={() => {
                                            resetForm();
                                            onClose();
                                        }}
                                        variant="outlined"
                                    >
                                        {t('common:cancel')}
                                    </Button>
                                    <Button
                                        data-target-id="manage-gallery-submit"
                                        color="primary"
                                        onClick={() => handleSubmit()}
                                        disabled={!isValid || !dirty || isSubmitting}
                                        loading={isSubmitting}
                                    >
                                        {values.id ? t('favorites:update') : t('common:create')}
                                    </Button>
                                </Box>
                            </Box>
                        </DialogActions>
                    </StyledDialog>
                </Form>
            )}
        </Formik>
    );
};

type MultipleUserPickerOptionCardRightAreaProps = {
    optionSelected: boolean;
};
const MultipleUserPickerOptionCardRightArea: React.FC<MultipleUserPickerOptionCardRightAreaProps> = ({
    optionSelected,
}) => {
    const { t } = useTranslation('common');
    return (
        <StyledFormControlLabel
            name="user"
            checked={optionSelected}
            label=""
            control={
                <Button variant={optionSelected ? 'outlined' : 'contained'}>
                    {optionSelected ? t('common:invited') : t('common:invite')}
                </Button>
            }
        />
    );
};
